import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, RootState } from '../../../../store/store';
import SelectOption, { SelectedValue } from '../../../../components/SelectOption/SelectOption';
import { CHANNEL_ACTIONS_BY_ROLE } from '../../../../constants/channel-allowed-actions';
import {
  changeChannelAction,
  ChangeChannelActionType,
  channelActions,
  // channelButtonActions,
  channelDisableOutputAction,
  // ChannelTcAction,
  // channelTcButtonActions,
  testChannelButtonAction,
  winampActions,
} from './channelActions';
import { CYCLE_MEASUREMENT_SYMBOL, EXTENDED_INTERVAL_UPDATE_DATA } from '../../../../constants/common';
import { channelStateText } from '../../../../constants/channel';
import { Form, Formik } from 'formik';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import { SingleValueForm } from '../../Components/SingleValueForm';
import { disableOption, enableOption, outputChangeOptions } from './constants';
import { ElementSize } from '../../../../types/element-size';
import i18n from '../../../../infrastructure/i18n';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import { IChannelData, IValueWithTime } from '../../../../types/channel-types';
import SettingsItem from '../../../../components/SettingItem/SettingItem';
import useSafeRequest from '../../../../hooks/useSafeRequest';

type ComponentProps = { controllerId: string; channel_number: number };
type DispatchProps = ReturnType<typeof mapDispatch>;
type StateProps = ReturnType<typeof mapState>;

type EditChannelProps = ComponentProps & StateProps & DispatchProps;

const getChannelActivityStatus = (isActive: boolean) => {
  if (isActive) {
    return i18n.t('statuses.activity.connected');
  }

  return i18n.t('statuses.activity.disconnected');
};

const getChannelOutputStatus = (disabled: boolean) => {
  if (disabled) {
    return i18n.t('statuses.output.disabled');
  }

  return i18n.t('statuses.output.enabled');
};

const ChannelEditorView = ({ user, channel, getChannel, reset, controllerId, channel_number }: EditChannelProps) => {
  // const [assignTc, setAssignTc] = useState<SelectedValue>(null);
  // const [removeTc, setRemoveTc] = useState<SelectedValue>(null);
  const [outputOpt, setOutputOpt] = useState<SelectedValue>(null);

  const allowedActionsForUser = useMemo(() => {
    if (!user?.memberData?.role) {
      return [];
    }

    return CHANNEL_ACTIONS_BY_ROLE[user.memberData.role];
  }, [user]);

  // const changeRemoveTc = (data: SelectedValue) => {
  //   setRemoveTc(data);
  // };
  // const changeAssignTc = (data: SelectedValue) => {
  //   setAssignTc(data);
  // };

  // const onChangeOption = (event: React.ChangeEvent<HTMLSelectElement>, changeAction: ChannelTcAction) => {
  //   const { value } = event.target;

  //   const selectedValue: SelectedValue = value as unknown as SelectedValue;

  //   if (changeAction === ChannelTcAction.ASSIGN) {
  //     changeAssignTc(selectedValue);
  //   }

  //   if (changeAction === ChannelTcAction.REMOVE) {
  //     changeRemoveTc(selectedValue);
  //   }
  // };

  const onChangeOutputOption = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    const selectedValue: SelectedValue = value as unknown as SelectedValue;

    setOutputOpt(selectedValue);
  };

  const setDefaultOption = (channel?: IChannelData) => {
    if (!channel) {
      return;
    }

    const disabledOption = channel.disabledOutput.value ? disableOption : enableOption;

    setOutputOpt(disabledOption);
  };

  const getChannelOption = async (params: { controller_id: string, channel_num: number, signal?: AbortSignal }) => {
    const { controller_id, channel_num, signal } = params;
    const responce = await getChannel({ controller_id, channel_num, signal });
    setDefaultOption(responce.channel);
  }

  const handleSubmit = (item: ChangeChannelActionType, val: any) => {
    const transformedValue = item?.transformValue ? item.transformValue(val[item.actionField]) : val[item.actionField];
    return changeChannelAction(controllerId, channel_number, item.actionName, String(transformedValue));
  }

  useSafeRequest({
    request: getChannelOption,
    params: { controller_id: controllerId, channel_num: channel_number },
    options: {
      deps: [controllerId, channel_number],
      reset,
      pulling: {
        timestamp: EXTENDED_INTERVAL_UPDATE_DATA,
        callBackParams: { controller_id: controllerId, channel_num: channel_number },
        callBack: getChannel,
      },
    }
  });

  if (!channel) {
    return (
      <div className="p-edit-channel pb-4 md:pb-25">
        <div className=" flex h-full w-full items-center justify-center">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="p-edit-channel pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">
            Edit Channel {channel.name.value} ({CYCLE_MEASUREMENT_SYMBOL[channel.units]}) (
            {getChannelActivityStatus(channel.isActive)})
          </h3>
        </div>

        <SettingsItem
          title="State"
          value={channelStateText[channel.state.value]}
          updatedAt={new Date(channel.state.timestamp)}
          className="p-7"
          form={
            <>
              {winampActions.map((item) => {
                if (!allowedActionsForUser.includes(item.actionName)) {
                  return null;
                }

                return (
                  <Formik
                    key={`action-${item.actionName}`}
                    initialValues={{ name: '' }}
                    onSubmit={() => changeChannelAction(controllerId, channel_number, item.actionName, '')}
                    validationSchema={item?.validationSchema}
                  >
                    {(props) => (
                      <Form className="flex flex-row items-center">
                        <InteractiveButton
                          key={`action-${item.actionName}`}
                          disabled={props.isSubmitting}
                          customType="min-h-fit min-w-fit max-h-fit max-w-fit border border-foggy-mist rounded active:border-primary cursor-pointer"
                          style={{ padding: '8px' }}
                          submit
                        >
                          <item.Icon key={item.actionName} size={ElementSize.XXS} iconClassName="p-1" />
                        </InteractiveButton>
                      </Form>
                    )}
                  </Formik>
                );
              })}
            </>
          }
        />

        {allowedActionsForUser.includes(testChannelButtonAction.actionName) && (
          <div className="flex w-full flex-row justify-between p-7">
            <div className="flex w-full"></div>
            <div className="flex w-full flex-row justify-start gap-5.5">
              <Formik
                initialValues={{ name: '' }}
                onSubmit={() =>
                  changeChannelAction(controllerId, channel_number, testChannelButtonAction.actionName, '')
                }
              >
                {(props) => (
                  <Form>
                    <InteractiveButton
                      text={testChannelButtonAction.labelText ?? 'Update'}
                      submit
                      isLoading={props.isSubmitting}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        )}

        {allowedActionsForUser.includes(channelDisableOutputAction.actionName) && (
          <SettingsItem
            className="p-7"
            title={channelDisableOutputAction?.labelText}
            value={getChannelOutputStatus(channel.disabledOutput.value)}
            updatedAt={new Date(channel.disabledOutput.timestamp)}
            form={
              <Formik
                initialValues={{ name: '' }}
                onSubmit={() =>
                  changeChannelAction(controllerId, channel_number, channelDisableOutputAction.actionName, outputOpt.id)
                }
              >
                {(props) => (
                  <Form className="flex-auto">
                    <SelectOption
                      options={outputChangeOptions}
                      selectedOption={outputOpt}
                      title="Output option"
                      name="disable-output-select"
                      onChange={onChangeOutputOption}
                    />
                    <InteractiveButton
                      text={channelDisableOutputAction.btnText ?? 'Update'}
                      submit
                      disabled={!outputOpt}
                      isLoading={props.isSubmitting}
                    />
                  </Form>
                )}
              </Formik>
            }
          />
        )}

        {channelActions.map((item) => {
          if (!allowedActionsForUser.includes(item.actionName)) {
            return null;
          }
          return (
            <SingleValueForm
              key={item.actionName}
              type={item.inputType}
              values={{
                [item.actionField]: channel[item.actionField]?.value,
              }}
              label={item.labelText}
              currentValues={channel[item.actionField] as IValueWithTime<string>}
              onSubmit={(val) => handleSubmit(item, val)}
              validationSchema={item?.validationSchema}
              suggestion={item?.suggestion}
            />
          );
        })}
        {/* 
        <div className="flex w-full flex-row justify-between p-7">
          <div className="flex w-full"></div>
          <div className="flex w-full flex-row justify-start gap-5.5">
            {channelButtonActions.map((item) => {
              if (!allowedActionsForUser.includes(item.actionName)) {
                return null;
              }
              return (
                <Formik
                  key={item.labelText}
                  initialValues={{ name: '' }}
                  onSubmit={() => changeChannelAction(controllerId, channel_number, item.actionName, '')}
                >
                  {(props) => (
                    <Form>
                      <InteractiveButton text={item.labelText ?? 'Update'} submit isLoading={props.isSubmitting} />
                    </Form>
                  )}
                </Formik>
              );
            })}
          </div>
        </div>

        <div className="flex w-full flex-row justify-between p-7">
          <div className="flex w-full"></div>
          <div className="flex w-full flex-col justify-start gap-5.5">
            {channelTcButtonActions.map((item) => {
              if (!allowedActionsForUser.includes(item.actionName)) {
                return null;
              }
              return (
                <Formik
                  key={item.labelText}
                  initialValues={{ name: '' }}
                  onSubmit={() =>
                    changeChannelAction(
                      controllerId,
                      channel_number,
                      item.actionName,
                      item.action === ChannelTcAction.ASSIGN ? assignTc.id : removeTc.id,
                    )
                  }
                >
                  {(props) => (
                    <Form>
                      <SelectOption
                        options={options}
                        selectedOption={item.action === ChannelTcAction.ASSIGN ? assignTc : removeTc}
                        title="Thermocouple Number"
                        name="hello world"
                        onChange={(e) => onChangeOption(e, item.action)}
                      />
                      <InteractiveButton text={item.labelText ?? 'Update'} submit isLoading={props.isSubmitting} />
                    </Form>
                  )}
                </Formik>
              );
            })}
          </div>
        </div> */}
      </div>
    </div>
  );
};
const mapState = (state: RootState) => ({
  user: state.user.profile,
  channel: state.channels.channel,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getChannel: dispatch.channels.getChannel,
  reset: dispatch.channels.clearChannel,
});

export const ChannelEditor = connect<StateProps, DispatchProps, ComponentProps>(
  mapState,
  mapDispatch,
)(ChannelEditorView);
